<template>

  <v-app>

    <Top v-if="isShowable" :user="user" :sioVar="sioVar" />
    <Left v-if="isShowable" :key="user_data_fetched" />
    <vue-page-transition name="fade-in-left">
      <router-view class="router-wrapper" :key="update_view" />
    </vue-page-transition>
    <SuggestionBox v-if="isShowable"></SuggestionBox>
    <main-blur-spinner/>

    <!-- Legal Assistant FAB Button - Dynamic Color (hidden on report views) -->
    <v-btn
      v-if="showLegalAssistant"
      fab
      fixed
      bottom
      right
      large
      :color="fabColor"
      :class="['legal-rag-fab', fabMode]"
      @click="legalAssistantOpen = true"
      :title="fabTitle"
      elevation="8"
      :key="`fab-${assistantMode}-${caseDataKey}`"
    >
      <v-icon size="28" color="white" class="fab-icon">{{ fabIcon }}</v-icon>
    </v-btn>

    <!-- Legal Assistant Drawer - Enhanced with Mode (hidden on report views) -->
    <LegalAssistantDrawer
      v-if="showLegalAssistant"
      v-model="legalAssistantOpen"
      :mode="assistantMode"
      :case-data="currentCaseData"
      :key="`drawer-${assistantMode}-${caseDataKey}`"
    />
    <!-- <PWAInstallPrompt v-if="isShowable" /> -->
    <AppToast />
  </v-app>
</template>

<script>
import auth from './services/auth'
import { getTokenRedirect, isLoggedIn, msalReady } from "@/services/auth/redirectLogin";
import { refreshPeriodicallyToken } from "@/services/auth/tokenReset";

import MainBlurSpinner from "./components/generalComponents/MainBlurSpinner.vue";

import graph from './services/graph'
import gsap from "gsap";
import Login from './components/generalComponents/Login'
import DetailsModal from '@/components/generalComponents/DetailsModal'
import Left from '@/components/generalComponents/Navigation/Left'
import Top from '@/components/generalComponents/Navigation/Top'
import person from "./apiCallers/promise_handler_db/person"
import SocketioService from '@/services/socketio.service.js';
import { refreshLogoutTimer } from "@/plugins/timer.js"
import $ from 'jquery';
import SuggestionBox from './components/generalComponents/SuggestionBox.vue';
import fileTaggingHandler from "./apiCallers/promise_handler_db/fileTagging"
import LegalAssistantDrawer from './components/generalComponents/LegalAssistantDrawer.vue';
import PWAInstallPrompt from './components/generalComponents/PWAInstallPrompt.vue';
import AppToast from './components/generalComponents/AppToast.vue';


export default {
  name: 'App',

  components: { Login, DetailsModal, Left, Top, SuggestionBox, MainBlurSpinner, LegalAssistantDrawer, PWAInstallPrompt, AppToast },

  data: function () {
    return {

      sioVar: null,

      // User account object synced with MSAL getAccount()
      user: null,
      // Access token fetched via MSAL for calling Graph API
      accessToken: '',

      // Legal Assistant state
      legalAssistantOpen: false,

      // Details fetched from Graph API, user object and photo
      graphDetails: null,
      graphPhoto: null,

      // Visibility toggles for the three details modal popups
      showUserDetails: false,
      showGraphDetails: false,
      showTokenDetails: false,


      accessToken: null,
      update_view: 1,
      user_data_fetched: 1,

      // Any errors
      error: '',

      suggestion_box_pop: false,
    }
  },
  computed: {
    isShowable: function () {
      // Access this.user first so Vue always tracks it as a dependency
      // (avoids short-circuit skipping reactive property access)
      const hasUser = this.user != null;
      return hasUser && isLoggedIn();
    },

    // Detect if we're on a report page (hide Legal Assistant on reports)
    isReportView() {
      const path = this.$route.path;
      return path.includes('-report');
    },

    // Show Legal Assistant only when showable and NOT on report views
    showLegalAssistant() {
      return this.isShowable && !this.isReportView;
    },

    // Detect if we're on a case page
    isOnCasePage() {
      const path = this.$route.path;
      return path.startsWith('/case/') || path.startsWith('/archived-case/');
    },

    // Current case from Vuex store
    currentCaseData() {
      // Explicitly access the state to ensure reactivity tracking
      const caseData = this.$store.state.current_selected_case;
      return caseData;
    },

    // Unique key that changes when case data changes (forces re-render)
    caseDataKey() {
      const caseData = this.currentCaseData;
      if (caseData && caseData.group_url) {
        return caseData.group_url;
      }
      return 'no-case';
    },

    // Assistant mode: 'general' or 'case'
    assistantMode() {
      // Access all reactive properties explicitly to ensure Vue tracks them
      const routePath = this.$route.path;
      const caseData = this.currentCaseData;
      const isCase = this.isOnCasePage;

      // Determine mode
      const mode = isCase && caseData ? 'case' : 'general';

      console.log('[Legal Assistant] assistantMode computed:', {
        routePath,
        isCase,
        hasCaseData: !!caseData,
        caseId: caseData && caseData.group_url,
        mode
      });

      return mode;
    },

    // FAB color: blue for case mode, dark for general mode
    fabColor() {
      return this.assistantMode === 'case'
        ? '#3325ae'  // Blue for case mode
        : '#0f111c'; // Dark (matches logo) for general mode
    },

    // FAB icon: gavel for case mode, scales for general mode
    fabIcon() {
      return this.assistantMode === 'case'
        ? 'mdi-gavel'  // Gavel icon for case mode
        : 'mdi-scale-balance';      // Law scales icon
    },

    // CSS class for FAB mode
    fabMode() {
      return `fab-${this.assistantMode}`;
    },

    // FAB tooltip title
    fabTitle() {
      if (this.assistantMode === 'case' && this.currentCaseData) {
        return `Pravni Asistent - Analiza predmeta ${this.currentCaseData.broj_predmeta || ''}`;
      }
      return 'Pravni Asistent - AI pretraživanje zakona';
    }
  },

  watch: {
    $route(to, from) {
      if (to != from) {
        refreshLogoutTimer();
        // console.log(this.$router.options.routes.map((el)=> el.name))
        this.$store.state.paginacija.subpages = [];
        this.$store.state.paginacija.back_link = '';
        this.update_view++;
        this.$store.state.event_details.popup_open = false;
        this.$store.state.release_notes_open = false;
        this.$store.state.rocista_tab_active = false;
        this.$store.state.edit_data = null;
        this.$store.state.last_page_name = from.name;
        this.$store.state.spis_active_pic = null;
        this.$store.state.spis_overlay_open = false;
        this.$store.state.login_load = false;

        if (!this.$store.state.event_details.popup_open) {
          gsap.to(".mainH", 0, { overflow: "auto" });
        }
        if (isLoggedIn()) {
          person.getSelf(this.$store.state.aToken).then((resp) => {
            if (resp.data.user_role.allowed_start_urls.length > 0) {
              if (!resp.data.user_role.allowed_start_urls.includes(to.name)) {
                this.$router.push(resp.data.user_role.default_route)
              }
            }
            this.$store.state.logged_in_user = resp.data;

          })
        }
        if (window.matchMedia("only screen and (max-width: 560px)").matches) {
          $('.mobile-menu input')[0].checked = false;
          $(".navigation").removeClass("opened");
          gsap.to('.nav-item-hover', 0.5, { display: 'none', opacity: 0, left: '4rem' })
          console.log("is mobile");
        }
        $(window).off();
      }
    },
    // Watch for case data changes to update Legal Assistant mode
    '$store.state.current_selected_case': {
      handler: function(newCaseData, oldCaseData) {
        console.log('[Legal Assistant] Case data changed:', {
          hasNewData: !!newCaseData,
          newCaseId: newCaseData && newCaseData.group_url,
          hasOldData: !!oldCaseData,
          oldCaseId: oldCaseData && oldCaseData.group_url
        });
        // Force re-render when case data changes
        this.$forceUpdate();
      },
      deep: true
    },
    // If user changes, make calls to Graph API
    'user': async function () {
      // this.fetchGraphDetails()
    },
  },

  async created() {
    // BUILD VERSION - Check if client has latest code
    const BUILD_VERSION = 'v2024-BUILD-001'; // INCREMENT THIS ON EACH DEPLOY
    const BUILD_TIME = '2024-02-03T12:00:00Z'; // UPDATE THIS ON EACH DEPLOY
    console.log('%c[App] BUILD VERSION: ' + BUILD_VERSION, 'background: #222; color: #bada55; font-size: 16px;');
    console.log('%c[App] BUILD TIME: ' + BUILD_TIME, 'background: #222; color: #bada55; font-size: 16px;');

    // PWA DEBUG & FORCE UPDATE - Check service worker status
    console.log('[App] ====== PWA DEBUG ======');
    console.log('[App] Is PWA installed:', window.matchMedia('(display-mode: standalone)').matches);
    console.log('[App] Service Worker supported:', 'serviceWorker' in navigator);
    if ('serviceWorker' in navigator) {
      const registration = await navigator.serviceWorker.getRegistration();
      console.log('[App] Service Worker registered:', !!registration);
      if (registration) {
        console.log('[App] SW scope:', registration.scope);
        console.log('[App] SW state:', registration.active ? registration.active.state : 'no active SW');

        // FORCE CHECK FOR UPDATES
        console.log('[App] Checking for SW updates...');
        registration.update().catch(err => console.log('[App] SW update check failed:', err));

        // Request SW version
        if (navigator.serviceWorker.controller) {
          const messageChannel = new MessageChannel();
          messageChannel.port1.onmessage = (event) => {
            console.log('[App] SW Version:', event.data.version);
            // If old version, force refresh
            if (event.data.version && !event.data.version.includes('v4')) {
              console.log('[App] OLD SW DETECTED! Clearing caches and reloading...');
              caches.keys().then(names => {
                names.forEach(name => caches.delete(name));
              }).then(() => {
                window.location.reload(true);
              });
            }
          };
          navigator.serviceWorker.controller.postMessage({ type: 'GET_VERSION' }, [messageChannel.port2]);
        }

        // Listen for new service worker
        registration.addEventListener('updatefound', () => {
          console.log('[App] New SW found, will activate on next load');
          const newWorker = registration.installing;
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              console.log('[App] New SW installed, sending skip waiting...');
              newWorker.postMessage({ type: 'SKIP_WAITING' });
            }
          });
        });
      }
    }
    console.log('[App] ========================');

    // Wait for MSAL v3 initialization before checking login status
    try {
      await msalReady;
    } catch (e) {
      console.error('[App] MSAL initialization failed:', e);
    }

    // Basic setup of MSAL helper with client id, or give up
    if (isLoggedIn()) {
      this.user = await getTokenRedirect();
      refreshPeriodicallyToken();
      this.accessToken = this.user.accessToken;
      this.$store.state.aToken = this.user.accessToken;
      console.log('[App] Token set in store, length:', this.accessToken ? this.accessToken.length : 0);

      await person.getSelf(this.accessToken).then((resp) => {
        SocketioService.setupSocketConnection(resp.data.uid, this.$i18n.locale);
        this.$store.state.logged_in_user = resp.data;
        console.log( resp.data)
        this.user_data_fetched++;
        this.sioVar = SocketioService;
        this.$store.state.sioVar = SocketioService;
      })
      await fileTaggingHandler.getAll().then((resp)=>{
        this.$store.state.availableFileTags =resp.data;
      })

    } else {
      this.error = 'VUE_APP_CLIENT_ID is not set, the app will not function!'
    }

    // Listen for authentication errors
    window.addEventListener('auth-error', this.handleAuthError);
  },

  beforeDestroy() {
    // Clean up event listener
    window.removeEventListener('auth-error', this.handleAuthError);
  },

  methods: {
    /**
     * Handle authentication errors from token service
     */
    handleAuthError(event) {
      console.error('[App] Auth error received:', event.detail);

      // Notify service worker to clear caches
      this.clearServiceWorkerCache();

      // Show toast notification
      if (this.$toast) {
        this.$toast.error(
          event.detail.message || 'Sesija je istekla. Molimo prijavite se ponovno.',
          {
            duration: 8000,
            action: {
              text: 'Prijava',
              onClick: () => {
                // Force re-login
                window.location.reload();
              }
            }
          }
        );
      } else {
        // Fallback to alert if toast not available
        alert(event.detail.message || 'Sesija je istekla. Molimo prijavite se ponovno.');
      }
    },

    /**
     * Clear service worker cache on auth errors
     */
    clearServiceWorkerCache() {
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        console.log('[App] Notifying service worker to clear cache');
        navigator.serviceWorker.controller.postMessage({ type: 'AUTH_ERROR' });
      }

      // Also clear browser caches directly
      if ('caches' in window) {
        caches.keys().then(cacheNames => {
          cacheNames.forEach(cacheName => {
            caches.delete(cacheName);
          });
          console.log('[App] Browser caches cleared');
        });
      }
    },

    // Get an access token and call graphGetSelf & graphGetPhoto
    async fetchGraphDetails() {
      if (!this.user) { return }

      try {
        // this.graphPhoto = await graph.getPhoto()
        // console.log(this.graphPhoto);
        // this.accessToken = graph.getAccessToken()
        // const msg = await axios.get(process.env.VUE_APP_API_ENDPOINT_STARTER + "/calendar/calendars", {params:{"uid" : this.accessToken},credentials: true})
        // console.log(msg)
        person.getFilteredUsers(this.accessToken);

        person.getSelf(this.accessToken).then((response) => {
          this.graphDetails = response.data
        });
      } catch (err) {
        this.error = err
      }

    }
  },
 
}
</script>

<style>
@import "./assets/css/style.css";
@import "./assets/css/forms.css";
@import "./assets/css/fileStyle.css";
@import "./assets/css/emailClient.css";
@import "./assets/css/modal.css";


@import url('https://fonts.googleapis.com/css2?family=Barlow:wght@300;400;500;700&display=swap');

/* Legal Assistant FAB Button */
.legal-rag-fab {
  bottom: 32px !important;
  right: 32px !important;
  z-index: 1000;
  width: 64px !important;
  height: 64px !important;
  transition: all 0.3s ease !important;
}

/* General mode - Dark FAB (matches logo) */
.fab-general {
  background: #0f111c !important;
  box-shadow: 0 8px 24px rgba(15, 17, 28, 0.35) !important;
}

.fab-general:hover {
  transform: scale(1.1);
  box-shadow: 0 12px 32px rgba(15, 17, 28, 0.45) !important;
}

.fab-general::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 50%;
  background: #0f111c;
  animation: fab-pulse 2s ease-in-out infinite;
  z-index: -1;
}

/* Case mode - Blue FAB */
.fab-case {
  background: #3325ae !important;
  box-shadow: 0 8px 24px rgba(51, 37, 174, 0.35) !important;
}

.fab-case:hover {
  transform: scale(1.1);
  box-shadow: 0 12px 32px rgba(51, 37, 174, 0.45) !important;
}

.fab-case::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 50%;
  background: #3325ae;
  animation: fab-pulse-blue 2s ease-in-out infinite;
  z-index: -1;
}

@keyframes fab-pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 0.5;
  }
  50% {
    transform: scale(1.2);
    opacity: 0;
  }
}

@keyframes fab-pulse-blue {
  0%, 100% {
    transform: scale(1);
    opacity: 0.5;
  }
  50% {
    transform: scale(1.2);
    opacity: 0;
  }
}

.fab-icon {
  position: relative;
  z-index: 1;
}
</style>